import { ball, body, coverBg, edge, fade, gloss, mix, n, rng, shadowInk, star, tint, warm, type ArtPal } from './kit';

/** Spaceport: a big ringed planet, a small moon, a friendly saucer and a comet in a calm star field. */
export function spaceportCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.purple, 0.16) : tint(P.purple, 0.84);
  const mid = P.dark ? mix(P.panel, P.blue, 0.14) : tint(P.blue, 0.88);
  const bottom = P.dark ? P.bg : tint(P.sky, 0.9);
  const bg = coverBg(p, P, top, mid, bottom, [450, 200]);

  const r = rng(5);
  const stars: string[] = [];
  for (let i = 0; i < 40; i++) {
    const x = 180 + r() * 460;
    const y = r() * 400;
    const s = 0.7 + r() * 1.6;
    stars.push(`<circle cx="${n(x)}" cy="${n(y)}" r="${n(s)}" fill="${P.dark ? P.ink : P.purple}" fill-opacity="${n(0.2 + r() * 0.5)}"/>`);
  }
  const twinkle = (x: number, y: number, s: number) =>
    `<polygon points="${star(x, y, s, s * 0.28, 4)}" fill="${P.gold}" fill-opacity=".9"/>`;

  const px = 452;
  const py = 200;
  const pr = 92;
  const ringBack = `<path d="M${px - 160} ${py}A160 42 0 0 1 ${px + 160} ${py}" transform="rotate(-14 ${px} ${py})" fill="none" stroke="url(#${p}rg)" stroke-width="16" stroke-linecap="round"/>`;
  const ringFront = `<path d="M${px + 160} ${py}A160 42 0 0 1 ${px - 160} ${py}" transform="rotate(-14 ${px} ${py})" fill="none" stroke="url(#${p}rg)" stroke-width="16" stroke-linecap="round"/>`;

  const defs =
    `<defs>${bg.defs}` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.18) +
    ball(`${p}pl`, P.orange, 0.42, 0.45) +
    ball(`${p}mn`, P.sky, 0.45, 0.45) +
    `<linearGradient id="${p}rg" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${tint(warm(P), 0.35)}"/><stop offset=".4" stop-color="${warm(P)}"/><stop offset=".8" stop-color="${mix(warm(P), P.orange, 0.5)}"/><stop offset="1" stop-color="${P.orange}"/></linearGradient>` +
    ball(`${p}dm`, P.teal, 0.5, 0.3) +
    body(`${p}uf`, P.purple, 0.4, 0.35, 0.3, 1) +
    `<linearGradient id="${p}ct" x1="1" y1="1" x2="0" y2="0"><stop offset="0" stop-color="${P.sky}" stop-opacity=".9"/><stop offset=".5" stop-color="${P.sky}" stop-opacity=".35"/><stop offset="1" stop-color="${P.sky}" stop-opacity="0"/></linearGradient>` +
    ball(`${p}cc`, tint(P.sky, 0.3), 0.6, 0.2) +
    `</defs>`;

  const bands =
    `<clipPath id="${p}clip"><circle cx="${px}" cy="${py}" r="${pr}"/></clipPath>` +
    `<g clip-path="url(#${p}clip)" fill="none" stroke-linecap="round">` +
    `<path d="M${px - 110} ${py - 40}Q${px} ${py - 64} ${px + 110} ${py - 30}" stroke="${tint(P.orange, 0.35)}" stroke-width="10" stroke-opacity=".55"/>` +
    `<path d="M${px - 110} ${py + 14}Q${px} ${py - 8} ${px + 110} ${py + 26}" stroke="${mix(P.orange, P.red, 0.45)}" stroke-width="14" stroke-opacity=".45"/>` +
    `<path d="M${px - 110} ${py + 58}Q${px} ${py + 40} ${px + 110} ${py + 66}" stroke="${tint(P.orange, 0.2)}" stroke-width="8" stroke-opacity=".4"/>` +
    `</g>`;

  const ufo =
    `<g transform="translate(568 318) rotate(-8)">` +
    `<ellipse cx="0" cy="34" rx="44" ry="7" fill="url(#${p}sh)"/>` +
    `<path d="M-24 -2C-24 -30 24 -30 24 -2Z" fill="url(#${p}dm)" stroke="${edge(P.teal, P)}" stroke-width="2"/>` +
    gloss(-9, -16, 7, 4, -35, 0.6) +
    `<ellipse cx="0" cy="4" rx="54" ry="15" fill="url(#${p}uf)" stroke="${edge(P.purple, P)}" stroke-width="2"/>` +
    `<circle cx="-30" cy="5" r="4" fill="${P.gold}"/><circle cx="0" cy="10" r="4" fill="${P.gold}"/><circle cx="30" cy="5" r="4" fill="${P.gold}"/>` +
    gloss(-20, -2, 20, 3, -4, 0.35) +
    `</g>`;

  return (
    defs +
    bg.body +
    stars.join('') +
    twinkle(300, 80, 9) +
    twinkle(610, 150, 7) +
    twinkle(360, 330, 6) +
    // comet
    `<path d="M252 58L364 124" stroke="url(#${p}ct)" stroke-width="14" stroke-linecap="round"/>` +
    `<circle cx="368" cy="126" r="12" fill="url(#${p}cc)"/>` +
    // planet with ring
    `<ellipse cx="${px + 20}" cy="${py + pr + 44}" rx="120" ry="14" fill="url(#${p}sh)"/>` +
    ringBack +
    `<circle cx="${px}" cy="${py}" r="${pr}" fill="url(#${p}pl)" stroke="${edge(P.orange, P)}" stroke-width="2"/>` +
    bands +
    gloss(px - 36, py - 44, 30, 14, -38, 0.42) +
    ringFront +
    // moon
    `<circle cx="590" cy="74" r="28" fill="url(#${p}mn)" stroke="${edge(P.sky, P)}" stroke-width="2"/>` +
    `<circle cx="598" cy="84" r="6" fill="${edge(P.sky, P)}" fill-opacity=".35"/><circle cx="582" cy="66" r="4" fill="${edge(P.sky, P)}" fill-opacity=".3"/>` +
    gloss(580, 62, 9, 5, -38, 0.55) +
    ufo
  );
}
